const SegmentBrowserClient = require("./SegmentBrowserClient.js");
const SegmentNodeClient = require("./SegmentNodeClient.js");
const SentryClient = require("./SentryClient.js");

const METRICS_PROVIDERS = Object.freeze({
  SEGMENT: "segment",
  SENTRY: "sentry"
});

const METRICS_RUNTIMES = Object.freeze({
  BROWSER: "browser",
  NODE: "node"
});

/**
 * Build the metrics client for the configured provider.
 *
 * Defaults to Segment (browser runtime) so existing behaviour is preserved
 * until a deployment explicitly opts in to Sentry via `provider: "sentry"`.
 *
 * @param {object} options
 * @param {string} [options.provider] One of METRICS_PROVIDERS (default segment).
 * @param {string} [options.runtime] One of METRICS_RUNTIMES (default browser).
 *   Selects the Segment implementation; ignored for the Sentry provider.
 * @param {object} [options.segment] Segment dependency: the browser
 *   `window.analytics` snippet, or an analytics-node instance for the node runtime.
 * @param {object} [options.sentry] Sentry SDK dependency.
 * @param {string[]} [options.allowedAttributes] Override the Sentry metric
 *   attribute allowlist; forwarded to SentryClient (ignored for Segment).
 * @param {(error: Error) => void} [options.onError] Optional hook for provider
 *   SDK failures; forwarded to the client (see {@link IMetricsClient}).
 * @returns {IMetricsClient}
 */
function createMetricsClient({
  provider,
  runtime = METRICS_RUNTIMES.BROWSER,
  segment,
  sentry,
  allowedAttributes,
  onError
} = {}) {
  const resolvedProvider = provider || METRICS_PROVIDERS.SEGMENT;

  switch (resolvedProvider) {
    case METRICS_PROVIDERS.SENTRY:
      return new SentryClient(sentry, { allowedAttributes, onError });
    case METRICS_PROVIDERS.SEGMENT:
      // Reject unknown runtimes rather than silently defaulting to browser — a
      // backend config typo would otherwise drop the per-call userId and send
      // malformed Segment events.
      switch (runtime) {
        case METRICS_RUNTIMES.NODE:
          return new SegmentNodeClient(segment, { onError });
        case METRICS_RUNTIMES.BROWSER:
          return new SegmentBrowserClient(segment, { onError });
        default:
          throw new Error(`Unknown metrics runtime: ${runtime}`);
      }
    default:
      throw new Error(`Unknown metrics provider: ${resolvedProvider}`);
  }
}

module.exports = { createMetricsClient, METRICS_PROVIDERS, METRICS_RUNTIMES };
